// =============================================================================
// LP Express — "Coffee Shop Lead Capture (Gaffle)" template content model.
//
// Imported from the Gaffle landing-template export's `coffee-shop` template
// (Temecula coffee-shop lead-capture page offering a 50%-off first-drink
// coupon revealed on sign-up). Like the Cold Drip / Quiz / Listicle /
// Advertorial templates, the ENTIRE page copy lives as ONE structured blob at
// `generatedContent.coffeeShop` (alongside `templateType: "coffee-shop"`),
// edited in place via the shared path-based `setText`/`setLinkUrl` ops (paths
// like ["coffeeShop","hero","headline"]).
//
// Like the Cold Drip template, generation does NOT require a Sim workflow: the
// export's copy is embedded verbatim below as the template's default content,
// so a new run persists it synchronously (a SIM_LP_COFFEE_SHOP_URL workflow
// can later take over async generation, mirroring cold-drip). Per-section copy
// regeneration is wired through a dedicated route (dynamic block keys
// `coffee-shop-<sectionKey>`, mirroring `cold-drip-<sectionKey>`).
//
// Lead capture: the page's two lead forms POST (on PUBLISHED pages only) to
// the public endpoint /api/public/lp-express/:brandSlug/:slug/leads, which
// stores the lead natively (lp_express_leads) and returns a coupon code
// revealed in an on-screen card. In the builder the forms are inert — the
// copy (labels, placeholders, button text, success card) stays editable.
// The export's Meta Pixel is intentionally OMITTED (per task spec).
//
// Imagery: the export's asset binaries were NOT included in the export file
// (filenames only), so photo surfaces are AI slots (coffeeShopImages, standard
// single-slot pipeline) with the usual media-picker/upload override; the reel
// video is a media-picker-only slot (no AI video).
// =============================================================================

// ── Section catalog (from the export's sections.json, in order) ─────────────

export const LP_COFFEE_SHOP_SECTIONS: { key: string; label: string }[] = [
  { key: "ticker", label: "Scrolling Ticker" },
  { key: "header", label: "Header (Logo)" },
  { key: "hero", label: "Hero + Lead Form" },
  { key: "drinks", label: "Featured Products" },
  { key: "reel", label: "Reel Video" },
  { key: "industryCallout", label: "Them vs Us Callout" },
  { key: "values", label: "Values" },
  { key: "pourBar", label: "Visit Us" },
  { key: "faq", label: "FAQ" },
  { key: "bottomCta", label: "Bottom CTA + Lead Form" },
  { key: "footer", label: "Footer" },
  { key: "stickyBar", label: "Sticky Bottom Bar" },
];

export const LP_COFFEE_SHOP_SECTION_KEYS = LP_COFFEE_SHOP_SECTIONS.map((s) => s.key);

export function lpCoffeeShopSectionLabel(key: string): string | undefined {
  return LP_COFFEE_SHOP_SECTIONS.find((s) => s.key === key)?.label;
}

// ── Content types ────────────────────────────────────────────────────────────

export interface LpCoffeeShopContent {
  ticker: string[];
  hero: {
    eyebrow: string;
    headline: string;
    body: string;
    bullets: string[];
    socialProof: string;
    imageCaption: string;
    imageLocation: string;
    imageAlt: string;
  };
  leadForm: {
    eyebrow: string;
    heading: string;
    fields: { label: string; placeholder: string }[];
    submit: string;
    submitPending: string;
    footnote: string;
    errors: { missingFields: string; shortPhone: string; generic: string };
    success: { heading: string; body: string };
    /** Editable coupon-code prefix; codes render as "<PREFIX>-XXXX". */
    couponPrefix: string;
  };
  drinks: {
    eyebrow: string;
    heading: string;
    intro: string;
    items: { name: string; tag: string; line: string }[];
  };
  reel: { eyebrow: string; heading: string; body: string; caption: string };
  industryCallout: {
    eyebrow: string;
    heading: string;
    intro: string;
    them: { label: string; items: string[] };
    us: { label: string; items: string[] };
  };
  values: {
    eyebrow: string;
    heading: string;
    items: { title: string; body: string }[];
  };
  pourBar: {
    badge: string;
    heading: string;
    intro: string;
    slides: { caption: string; alt: string }[];
    stats: { num: string; label: string }[];
    doorSign: {
      header: string;
      openBanner: string;
      hoursLabel: string;
      hours: { day: string; hours: string }[];
      addressCallout: string;
      address: string;
      mapsCta: string;
      mapsUrl: string;
    };
    contacts: {
      phoneLabel: string;
      phone: string;
      phoneHref: string;
      emailLabel: string;
      email: string;
      emailHref: string;
    };
  };
  faq: { eyebrow: string; heading: string; items: { q: string; a: string }[] };
  bottomCta: { eyebrow: string; heading: string; body: string };
  footer: {
    tagline: string;
    links: { label: string; href: string }[];
    copyright: string;
    signoff: string;
  };
  stickyBar: { label: string; cta: string };
}

// ── Default content (VERBATIM from the export's content.json) ───────────────

export const LP_COFFEE_SHOP_DEFAULT_CONTENT: LpCoffeeShopContent = {
  ticker: [
    "50% OFF YOUR FIRST BEVERAGE!",
    "SMALL-BATCH · TEMECULA, CA",
    "LOWER ACIDITY · CLEANER CAFFEINE",
    "STAY SHAKING",
    "CLASSIC · BLUE MAGIC · DECAF · ON TAP",
    "OPEN MON–SAT · 8AM – 5PM",
  ],
  hero: {
    eyebrow: "Temecula, CA · Now open",
    headline: "50% off your first cold drip.",
    body: "Drop your info below and your one-time coupon code pops up on screen instantly. Screenshot it and bring it to the shop. Cold drip on tap, signature cold pours, small-batch everything.",
    bullets: [
      "Half off your first drink with this coupon",
      "Coupon appears on screen instantly · no membership",
      "Use it any time we're open · 8am–5pm, Mon–Sat",
    ],
    socialProof: "4.9 stars · 600+ Google reviews in Temecula, CA",
    imageCaption: "Fresh from the tap",
    imageLocation: "Temecula, CA",
    imageAlt: "A cold drip pour in a Gaffle cup, fresh from the tap",
  },
  leadForm: {
    eyebrow: "50% off · one per person",
    heading: "Get a 50% off coupon.",
    fields: [
      { label: "First name", placeholder: "Name" },
      { label: "Email", placeholder: "you@email.com" },
      { label: "Phone", placeholder: "(555) 123-4567" },
    ],
    submit: "Get My 50% Off Coupon",
    submitPending: "Sending…",
    footnote:
      "Your coupon code shows up right here after you submit — just screenshot it and bring it in.",
    errors: {
      missingFields: "Please fill in all three fields.",
      shortPhone: "That phone number looks too short.",
      generic: "Something went wrong. Please try again.",
    },
    success: {
      heading: "You're in!",
      body: "Show this coupon to your barista on your next visit. Good for 50% off any beverage.",
    },
    couponPrefix: "WELCOME50",
  },
  drinks: {
    eyebrow: "The menu, briefly",
    heading: "What's on tap.",
    intro: "Four drinks worth crossing town for. Everything else is on the menu inside.",
    items: [
      {
        name: "The Classic Cold Drip",
        tag: "House favorite",
        line: "Slow-dripped over 8 hours. Smooth, full-bodied, naturally sweet.",
      },
      {
        name: "Iced Capooch",
        tag: "Steady focus",
        line: "Gaffle's signature drink: cold drip concentrate, milk, maple syrup, and maple cinnamon cold foam.",
      },
      {
        name: "Cold Drip Latte",
        tag: "Local pick",
        line: "Cold drip concentrate with your choice of milk.",
      },
      {
        name: "Dubai Koffee",
        tag: "Secret menu",
        line: 'Part of the "not so secret" secret menu: cold drip concentrate, milk, pistachio syrup, dubai cold foam, cardamom, cacao, kataifi, and pistachio.',
      },
    ],
  },
  reel: {
    eyebrow: "From the shop",
    heading: "See us pour.",
    body: "A peek inside the pour bar — straight off the tower, into the cup. Stop in and watch yours pour.",
    caption: "Temecula, CA",
  },
  industryCallout: {
    eyebrow: "A quiet little industry secret",
    heading: 'Most "cold brew" is a shortcut.',
    intro:
      "Walk into the average café and that iced coffee on the menu is usually commodity beans, a concentrate poured from a bag, and syrup doing the heavy lifting. We refuse to play that game.",
    them: {
      label: "The other guys",
      items: [
        "Commodity beans bought on price, not flavor",
        "Pre-made concentrate from a wholesale bag",
        "Brewed hot, then chilled — sour, bitter, flat",
        "Sugar and syrup hiding what's in the cup",
        "No idea where the coffee actually came from",
      ],
    },
    us: {
      label: "Gaffle",
      items: [
        "Specialty-grade single-origin beans, sourced direct",
        "Roasted in small batches at our own roastery",
        "Slow-dripped cold for 12 hours — never heat-touched",
        "Smooth, low-acid, jitter-free — no syrup needed",
        "We can tell you the farm, the harvest, the altitude",
      ],
    },
  },
  values: {
    eyebrow: "The Gaffle difference",
    heading: "Why our cold coffee tastes different.",
    items: [
      {
        title: "Slow-Dripped, Always",
        body: "Cold drip — not cold brew. Water beads through a tower for 8+ hours. The result is smoother, less acidic, and naturally sweeter than anything steeped in a bucket.",
      },
      {
        title: "Small-Batch, Local",
        body: "Small-batch beans, slow-dripped and served fresh in our Temecula shop. Pulled straight off the tower into your cup.",
      },
      {
        title: "Cleaner Caffeine",
        body: "Lower acidity than hot brew, no syrups required. Pair Blue Magic with naturally-occurring L-theanine for the focus curve hot coffee can't touch.",
      },
    ],
  },
  pourBar: {
    badge: "Visit us · Temecula, CA",
    heading: "This isn't a factory. It's a pour bar.",
    intro:
      "Real address. Real cold drip. Walk in, meet the crew, and try every flavor before you commit to a bottle.",
    slides: [
      {
        caption: "Pour bar · both blends on tap",
        alt: "Gaffle cold drip tap handles in red and blue, Classic and Blue Magic",
      },
      {
        caption: "The Gaffle menu wall",
        alt: "The Gaffle cafe menu painted on the wall, listing Cold Brew, Cold Drip Latte, and Signature Latte options",
      },
      {
        caption: "Inside the pour bar",
        alt: "Inside the Gaffle pour bar, center island with bottles, cold case, and pour bar in the background",
      },
    ],
    stats: [
      { num: "12hr", label: "Cold drip per batch" },
      { num: "3", label: "Cups always pouring" },
      { num: "20%", label: "Lower acidity" },
    ],
    doorSign: {
      header: "Gaffle Pour Bar",
      openBanner: "Pouring Now",
      hoursLabel: "Hours of Operation",
      hours: [
        { day: "Monday", hours: "8 AM – 5 PM" },
        { day: "Tuesday", hours: "8 AM – 5 PM" },
        { day: "Wednesday", hours: "8 AM – 5 PM" },
        { day: "Thursday", hours: "8 AM – 5 PM" },
        { day: "Friday", hours: "8 AM – 5 PM" },
        { day: "Saturday", hours: "8 AM – 5 PM" },
        { day: "Sunday", hours: "Closed" },
      ],
      addressCallout: "We have a real address. Come pour with us.",
      address: "27725 Jefferson Ave STE 103, Temecula, CA 92590",
      mapsCta: "Tap to open in Maps",
      mapsUrl: "https://maps.google.com/?q=27725+Jefferson+Ave+STE+103+Temecula+CA+92590",
    },
    contacts: {
      phoneLabel: "Call ahead",
      phone: "(404) 490-1143",
      phoneHref: "tel:+14044901143",
      emailLabel: "Wholesale",
      email: "hello@gaffle.coffee",
      emailHref: "mailto:hello@gaffle.coffee",
    },
  },
  faq: {
    eyebrow: "Common questions",
    heading: "Before you stop by.",
    items: [
      {
        q: "How do I claim my 50% off coupon?",
        a: "Just sign up below — your one-time coupon code will pop up on screen the moment you submit. Screenshot it and show the barista on your next visit to get 50% off your first drink.",
      },
      {
        q: "Where are you located?",
        a: "27725 Jefferson Ave STE 103, Temecula, CA 92590. Two blocks from the roundabout, look for the red awning. Parking out front and around the corner.",
      },
      {
        q: "What's the difference between cold drip and cold brew?",
        a: "Cold brew is steeped in cold water for 12–24 hours. Cold drip uses a tower that releases water drop-by-drop through the grounds over 8+ hours. It's a cleaner extraction — less bitter, brighter, sweeter, with about 20% lower acidity.",
      },
      {
        q: "Do you serve hot drinks?",
        a: "No — Gaffle is a cold coffee concept. Cold drip, cold brew, iced lattes, and signature cold pours only. No espresso, no hot drinks.",
      },
      {
        q: "Can I take bottles home?",
        a: "Always. Grab bottles straight from the shop fridge, or have them shipped from gaffle.coffee. While you're in, browse our caffeine-infused coffee skincare — made in-house with the same beans we drip.",
      },
    ],
  },
  bottomCta: {
    eyebrow: "Still here?",
    heading: "Grab your 50% off coupon.",
    body: "One coupon per phone number. It appears on screen the moment you sign up — just screenshot it and bring it in. No spam, no surprises.",
  },
  footer: {
    tagline: "Specialty cold coffee, slow-dripped and served fresh in Temecula, California.",
    links: [
      { label: "Shop", href: "https://gaffle.coffee/" },
      { label: "FAQ", href: "https://gaffle.coffee/pages/faq" },
      { label: "Contact Us", href: "https://gaffle.coffee/policies/contact-information" },
    ],
    copyright: "© {year} Gaffle LLC. All rights reserved.",
    signoff: "Stay shaking.",
  },
  stickyBar: { label: "· 50% off your first drink", cta: "Get 50% Off" },
};

// ── Normalization ────────────────────────────────────────────────────────────
// Same type-preserving overlay as the cold-drip template: raw values are
// accepted only when they match the default's type at the same position;
// anything missing or mistyped falls back to the default. NOTE: any new
// editable field must be added to LpCoffeeShopContent AND
// LP_COFFEE_SHOP_DEFAULT_CONTENT or it will be dropped here.

function overlay(def: any, raw: any): any {
  if (typeof def === "string") return typeof raw === "string" ? raw : def;
  if (typeof def === "number") {
    const n = typeof raw === "number" ? raw : Number(raw);
    return Number.isFinite(n) ? n : def;
  }
  if (typeof def === "boolean") return typeof raw === "boolean" ? raw : def;
  if (Array.isArray(def)) {
    if (!Array.isArray(raw)) return def;
    const template = def[0];
    if (template === undefined) return raw;
    if (typeof template === "string") {
      return raw.filter((x) => typeof x === "string" && x.trim()).slice(0, 40);
    }
    return raw
      .filter((x) => x && typeof x === "object" && !Array.isArray(x))
      .map((x) => overlay(template, x))
      .slice(0, 40);
  }
  if (def && typeof def === "object") {
    const src = raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(def)) out[k] = overlay(def[k], (src as any)[k]);
    return out;
  }
  return raw ?? def;
}

export function normalizeLpCoffeeShopContent(raw: unknown): LpCoffeeShopContent {
  return overlay(LP_COFFEE_SHOP_DEFAULT_CONTENT, raw) as LpCoffeeShopContent;
}

// True when the blob carries enough structure to render the page.
export function isLpCoffeeShopContentUsable(
  cs: LpCoffeeShopContent | null | undefined,
): boolean {
  return Boolean(cs && cs.hero?.headline && cs.leadForm?.heading);
}

// ── Regen block keys ─────────────────────────────────────────────────────────
// Per-section copy regen uses dynamic block keys "coffee-shop-<sectionKey>"
// (resolved by lpCopyBlockLabel in shared/lp-express-sections.ts), mirroring
// the cold-drip template's "cold-drip-<sectionKey>" precedent.

export function lpCoffeeShopRegenKey(sectionKey: string): string {
  return `coffee-shop-${sectionKey}`;
}

export const LP_COFFEE_SHOP_REGEN_KEY_RE = /^coffee-shop-([A-Za-z]+)$/;

// ── Coupon codes ─────────────────────────────────────────────────────────────
// Native coupon generation for the lead-capture flow: an editable prefix from
// the page copy (leadForm.couponPrefix) plus a short random suffix. Kept here
// (shared, dependency-free) so both the server route (generation) and the
// client (display fallback) agree on the shape.

const COUPON_SUFFIX_ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789"; // no 0/O/1/I/L

export function lpCoffeeShopCouponCode(prefix: string): string {
  const clean = (prefix || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 20) || "WELCOME50";
  let suffix = "";
  for (let i = 0; i < 4; i++) {
    suffix += COUPON_SUFFIX_ALPHABET[Math.floor(Math.random() * COUPON_SUFFIX_ALPHABET.length)];
  }
  return `${clean}-${suffix}`;
}

// ── AI image slots ───────────────────────────────────────────────────────────
// The export's photo binaries were not included, so photo surfaces render
// through the standard LP Express single-slot pipeline
// (SINGLE_SLOT_IMAGE_CONFIGS "coffeeShopImages" in lp-express-worker.ts),
// stored on generatedContent.coffeeShopImages, each with the usual
// media-picker/upload override. The reel VIDEO is a media-picker-only slot
// (mediaOverrides key "coffee-shop-reel") — no AI video generation.
//   hero-photo   → hero image card (4:3)
//   drink-<i>    → drinks gallery cards (1:1, up to 4 — count follows copy)
//   pourbar-<i>  → visit-the-shop carousel slides (4:3, up to 3)
export const LP_COFFEE_SHOP_IMAGE_SLOTS: {
  key: string;
  variantHint: string;
  aspectRatio?: string;
}[] = [
  {
    key: "hero-photo",
    aspectRatio: "4:3",
    variantHint:
      "Hero photo: the brand's signature product being used or enjoyed, up close and appealing, in its natural setting; warm editorial photography, landscape composition filling the entire frame edge-to-edge, no borders or white margins, no text overlays.",
  },
  {
    key: "drink-0",
    aspectRatio: "1:1",
    variantHint:
      "Featured product photo #1: the FIRST featured product/item from the page copy, styled solo on a clean warm background; appealing product photography, square composition filling the entire frame edge-to-edge, no borders or white margins, no text overlays.",
  },
  {
    key: "drink-1",
    aspectRatio: "1:1",
    variantHint:
      "Featured product photo #2: the SECOND featured product/item from the page copy (visually distinct from photo #1), styled solo on a clean warm background; appealing product photography, square composition filling the entire frame edge-to-edge, no borders or white margins, no text overlays.",
  },
  {
    key: "drink-2",
    aspectRatio: "1:1",
    variantHint:
      "Featured product photo #3: the THIRD featured product/item from the page copy (visually distinct from photos #1–#2), styled solo on a clean warm background; appealing product photography, square composition filling the entire frame edge-to-edge, no borders or white margins, no text overlays.",
  },
  {
    key: "drink-3",
    aspectRatio: "1:1",
    variantHint:
      "Featured product photo #4: the FOURTH featured product/item from the page copy (visually distinct from photos #1–#3), styled solo on a clean warm background; appealing product photography, square composition filling the entire frame edge-to-edge, no borders or white margins, no text overlays.",
  },
  {
    key: "pourbar-0",
    aspectRatio: "4:3",
    variantHint:
      "Behind-the-scenes photo #1: where the brand's product is made, packed, or presented — workspace, materials, or process in warm natural light; documentary photography, landscape composition filling the entire frame edge-to-edge, no borders or white margins, no text overlays.",
  },
  {
    key: "pourbar-1",
    aspectRatio: "4:3",
    variantHint:
      "Behind-the-scenes photo #2: the brand's product lineup, ingredients, or display arranged with warm inviting atmosphere (distinct from photo #1); documentary photography, landscape composition filling the entire frame edge-to-edge, no borders or white margins, no legible invented text.",
  },
  {
    key: "pourbar-2",
    aspectRatio: "4:3",
    variantHint:
      "Behind-the-scenes photo #3: a wide view of the brand's world — the space, people, or setting where the product lives, warm and welcoming (distinct from photos #1–#2); documentary photography, landscape composition filling the entire frame edge-to-edge, no borders or white margins, no text overlays.",
  },
];

// Seed (and background-fill) only the slots the page's copy blob actually
// renders — the drinks gallery card count follows the copy (up to 4), and the
// visit carousel follows the slides array (up to 3).
export function coffeeShopImageSlotsFor(content: {
  drinks?: { items?: unknown[] };
  pourBar?: { slides?: unknown[] };
}): { key: string; variantHint: string; aspectRatio?: string }[] {
  const items = Array.isArray(content?.drinks?.items)
    ? (content.drinks.items as { name?: string; line?: string }[])
    : [];
  const slides = Array.isArray(content?.pourBar?.slides)
    ? (content.pourBar.slides as { caption?: string; alt?: string }[])
    : [];
  const drinkCount = Math.min(items.length, 4);
  const slideCount = Math.min(slides.length, 3);
  return LP_COFFEE_SHOP_IMAGE_SLOTS.filter((s) => {
    const d = /^drink-(\d+)$/.exec(s.key);
    if (d) return Number(d[1]) < drinkCount;
    const p = /^pourbar-(\d+)$/.exec(s.key);
    if (p) return Number(p[1]) < slideCount;
    return true;
  }).map((s) => {
    // Ground each card's image in the card's ACTUAL copy (same fix as the
    // cold-drip ingredient cards): the static hints only say "the Nth menu
    // item", which gave the prompt workflow nothing to differentiate the
    // cards with — every card came back as the same generic brand scene.
    const d = /^drink-(\d+)$/.exec(s.key);
    if (d) {
      const item = items[Number(d[1])];
      const name = typeof item?.name === "string" ? item.name.trim() : "";
      if (!name) return s;
      const line = typeof item?.line === "string" ? item.line.trim() : "";
      return {
        ...s,
        variantHint:
          `Featured product photo #${Number(d[1]) + 1}: the subject of THIS card is "${name}"` +
          (line ? ` (${line})` : "") +
          `. Show that exact item, styled solo on a clean warm background, so the photo is unmistakably ${name} and clearly different from the page's other featured-product photos; appealing product photography, square composition filling the entire frame edge-to-edge, no borders or white margins, no text overlays.`,
      };
    }
    const p = /^pourbar-(\d+)$/.exec(s.key);
    if (p) {
      const slide = slides[Number(p[1])];
      const subject =
        (typeof slide?.alt === "string" && slide.alt.trim()) ||
        (typeof slide?.caption === "string" && slide.caption.trim()) ||
        "";
      if (!subject) return s;
      return {
        ...s,
        variantHint: `${s.variantHint} This slide is specifically about: "${subject}" — make that the focus so it reads clearly different from the other behind-the-scenes photos.`,
      };
    }
    return s;
  });
}

// ── Brand-facts sanitizer ────────────────────────────────────────────────────
// The default blob is the literal Gaffle export. This deterministic pass runs
// server-side on EVERY generated blob and:
//   • blanks location/contact facts we have no brand data for (address, maps
//     URL, phone, email, hours callouts stay editable in the builder);
//   • points every gaffle.coffee URL at the brand's own site;
//   • replaces any remaining "Gaffle" token in copy with the brand name and
//     drops the Temecula location references.
// A page generated FOR the Gaffle brand itself is left untouched.

export interface CoffeeShopBrandFacts {
  brandName: string;
  brandUrl?: string | null;
}

function csNormalizeUrl(raw: unknown): string {
  const s = typeof raw === "string" ? raw.trim() : "";
  if (!s) return "";
  return /^https?:\/\//i.test(s) ? s : `https://${s}`;
}

function csMapStrings(node: unknown, fn: (s: string) => string): unknown {
  if (typeof node === "string") return fn(node);
  if (Array.isArray(node)) return node.map((x) => csMapStrings(x, fn));
  if (node && typeof node === "object") {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(node as Record<string, unknown>)) {
      out[k] = csMapStrings((node as Record<string, unknown>)[k], fn);
    }
    return out;
  }
  return node;
}

export function coffeeShopBrandFactsFromRows(brand: any): CoffeeShopBrandFacts {
  return {
    brandName: String(brand?.name || ""),
    brandUrl: brand?.url ?? null,
  };
}

export function applyCoffeeShopBrandFacts(
  content: LpCoffeeShopContent,
  facts: CoffeeShopBrandFacts,
): LpCoffeeShopContent {
  const brandName = (facts.brandName || "").trim();
  // The reference brand's own pages keep their real facts.
  if (!brandName || /gaffle/i.test(brandName)) return content;

  const siteUrl = csNormalizeUrl(facts.brandUrl);
  const siteHost = siteUrl ? siteUrl.replace(/^https?:\/\/(www\.)?/i, "").replace(/\/.*$/, "") : "";

  // ── Structured facts first (so the generic scrub never sees them) ──
  // Location/contact facts we have no brand data for: blank, never false.
  if (/temecula/i.test(content.pourBar.badge)) content.pourBar.badge = "Visit us";
  if (/pour bar|gaffle/i.test(content.pourBar.doorSign.header)) {
    content.pourBar.doorSign.header = brandName;
  }
  if (/pour/i.test(content.pourBar.doorSign.openBanner)) {
    content.pourBar.doorSign.openBanner = "Open Now";
  }
  if (/pour/i.test(content.pourBar.doorSign.addressCallout)) {
    content.pourBar.doorSign.addressCallout = "Come say hi in person.";
  }
  content.pourBar.doorSign.address = "";
  content.pourBar.doorSign.mapsUrl = "";
  content.pourBar.doorSign.mapsCta = "";
  content.pourBar.contacts.phone = "";
  content.pourBar.contacts.phoneHref = "";
  content.pourBar.contacts.email = "";
  content.pourBar.contacts.emailHref = "";
  content.pourBar.contacts.phoneLabel = "";
  content.pourBar.contacts.emailLabel = "";
  // Gaffle's opening hours are a FACT we have no brand data for (and a model
  // "rewrite" would just fabricate hours). Keep the day rows editable but
  // blank the values — the renderer hides blank rows on the public page.
  content.pourBar.doorSign.hours = content.pourBar.doorSign.hours.map((h) => ({
    ...h,
    hours: "",
  }));
  // Cafe-residue backstop: the copy model is instructed to reframe every
  // coffee concept around the actual brand, but when a stat / slide caption
  // still reads as cafe copy ("cold drip per batch", "menu wall", "on tap"),
  // blank it rather than ship Gaffle's shop on another brand's page. Blank
  // stats/captions are hidden on the public page and editable in the builder.
  const cafeResidue =
    /\b(?:cold[\s-]?drip|cold[\s-]?brew|coffee|cafe|caf\u00e9|espresso|latte|brew(?:ing)?|pour(?:ing)?(?:\s+bar)?|on\s+tap|taps?|cups?|acidity|menu\s+wall|barista)\b/i;
  content.pourBar.stats = content.pourBar.stats.map((st) =>
    cafeResidue.test(`${st.num} ${st.label}`) ? { num: "", label: "" } : st,
  );
  content.pourBar.slides = content.pourBar.slides.map((s) => ({
    ...s,
    caption: cafeResidue.test(s.caption) ? "" : s.caption,
    alt: cafeResidue.test(s.alt) ? "" : s.alt,
  }));
  if (cafeResidue.test(content.pourBar.heading)) {
    content.pourBar.heading = `Behind the scenes at ${brandName}.`;
  }
  if (cafeResidue.test(content.pourBar.intro)) {
    content.pourBar.intro = `A look at the people and the process behind ${brandName}.`;
  }
  // The location FAQ's answer is Gaffle's real address + directions — replace
  // wholesale (a token scrub would leave dangling directions).
  content.faq.items = content.faq.items.map((it) =>
    /where are you located/i.test(it.q)
      ? {
          ...it,
          a: `You can find ${brandName} online${siteHost ? ` at ${siteHost}` : ""} — grab your coupon above and use it on your first order.`,
        }
      : it,
  );
  if (/gaffle/i.test(content.footer.copyright)) {
    content.footer.copyright = `© {year} ${brandName}. All rights reserved.`;
  }
  // Footer links point at the brand's own site (or blank → renderer hides).
  content.footer.links = content.footer.links.map((l) =>
    /gaffle\.coffee/i.test(l.href) ? { ...l, href: siteUrl || "" } : l,
  );

  // ── Generic token scrub across every remaining string ──
  const scrub = (input: string): string => {
    let s = input;
    s = s.replace(/https?:\/\/(?:www\.)?gaffle\.coffee[^\s"'<>)]*/gi, siteUrl || "");
    s = s.replace(/[\w.+-]+@gaffle\.coffee/gi, "");
    s = s.replace(/\bgaffle\.coffee\b/gi, siteHost || brandName);
    s = s.replace(/\bgaffle('s)?\b/gi, (_m, poss) => (poss ? `${brandName}'s` : brandName));
    // Gaffle's home town is a fact, not copy — drop it from prose.
    s = s.replace(/\s*(?:in\s+)?Temecula(?:\s*,\s*(?:CA(?:lifornia)?\.?))?/gi, "");
    // Tidy leftover separators from removed fragments.
    s = s.replace(/\s{2,}/g, " ").replace(/\s+([,.])/g, "$1");
    s = s.replace(/^[·,]\s*/, "").replace(/[·,]\s*$/g, "").trim();
    return s;
  };
  return csMapStrings(content, scrub) as LpCoffeeShopContent;
}
